/**
 * As notificações partidas em faixas de tempo: hoje, ontem, esta semana, antes.
 *
 * <p>A lista chega com até 200 itens em ordem decrescente, e era desenhada como uma
 * parede única. Numa parede assim, "o que aconteceu desde ontem" — que é a pergunta que
 * traz alguém à tela — só se responde comparando data por data com o dedo.
 *
 * <p><b>Os cortes são de calendário, e não múltiplos de 24 horas.</b> Uma notificação de
 * ontem às 23h é de ontem, mesmo que faça duas horas. É a mesma decisão da régua de
 * presença da Equipe, e pelo mesmo motivo: contar em janelas de 24h faz o rótulo mudar no
 * meio da madrugada, sem nada ter acontecido.
 *
 * <p>Mora fora do React porque a fronteira de dia é exatamente o tipo de conta que erra
 * uma vez por dia, à meia-noite, e ninguém está olhando na hora. Fora da tela, ela tem
 * teste.
 */

export type FaixaDeTempo = 'hoje' | 'ontem' | 'semana' | 'antes';

export const ROTULO_DA_FAIXA: Record<FaixaDeTempo, string> = {
  hoje: 'Hoje',
  ontem: 'Ontem',
  semana: 'Esta semana',
  antes: 'Antes',
};

/** O dia de calendário de uma data, sem a hora. */
function soODia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Em que faixa uma data cai.
 *
 * <p>Data ilegível — ou ausente — cai em "antes". `criadoEm` já voltou nulo da API cinco
 * vezes neste projeto, e mandar a notificação para o fim da lista é melhor que quebrar a
 * tela ou inventar que ela é de hoje.
 *
 * <p>O futuro conta como hoje: relógio do cliente atrasado em relação ao servidor não
 * pode criar uma quinta faixa acima de "Hoje".
 */
export function faixaDe(iso: string | null | undefined, agora = new Date()): FaixaDeTempo {
  if (!iso) return 'antes';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return 'antes';

  const dias = Math.round((soODia(agora).getTime() - soODia(quando).getTime()) / 86_400_000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 7) return 'semana';
  return 'antes';
}

/**
 * Agrupa mantendo a ordem em que os itens chegaram.
 *
 * <p>Devolve só as faixas que têm alguém — um título "Ontem" sobre o vazio é ruído —, e
 * sempre na ordem do mais recente para o mais antigo, que é a da lista de origem.
 *
 * <p>Não ordena nada: a API já manda decrescente, e reordenar aqui seria a segunda régua
 * para a mesma decisão.
 */
export function agrupaPorFaixa<T>(
  itens: T[],
  dataDe: (item: T) => string | null | undefined,
  agora = new Date()
): { faixa: FaixaDeTempo; rotulo: string; itens: T[] }[] {
  const ordem: FaixaDeTempo[] = ['hoje', 'ontem', 'semana', 'antes'];
  const baldes = new Map<FaixaDeTempo, T[]>();

  for (const item of itens) {
    const faixa = faixaDe(dataDe(item), agora);
    const balde = baldes.get(faixa);
    if (balde) balde.push(item);
    else baldes.set(faixa, [item]);
  }

  return ordem
    .filter((faixa) => baldes.has(faixa))
    .map((faixa) => ({ faixa, rotulo: ROTULO_DA_FAIXA[faixa], itens: baldes.get(faixa)! }));
}
